import { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { saluteLegend, unsaluteLegend, formatNumber } from '../../services/legendApi';
import styles from './SaluteButton.module.css';

/**
 * 致敬按钮组件 - 将"点赞"升级为"致敬"
 *
 * 特点：
 * - 使用 🤟 摇滚手势符号
 * - 点击时有火焰粒子特效
 * - 可选引擎轰鸣声效
 * - 支持权重显示（高级骑士致敬权重更高）
 */
export default function SaluteButton({
    legendId,
    initialCount = 0,
    initialSaluted = false,
    size = 'medium', // small | medium | large
    showCount = true,
    showText = false,
    onSalute,
    className = '',
}) {
    const { isLoggedIn, openAuthModal } = useAuth();
    const { language } = useLanguage();

    const [saluted, setSaluted] = useState(initialSaluted);
    const [count, setCount] = useState(initialCount);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // 处理致敬点击
    const handleClick = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();

        // 未登录则打开登录框
        if (!isLoggedIn) {
            openAuthModal?.();
            return;
        }

        if (isLoading) return;

        setIsLoading(true);

        try {
            if (saluted) {
                // 取消致敬
                const result = await unsaluteLegend(legendId);
                if (result.success) {
                    setSaluted(false);
                    setCount(prev => Math.max(0, prev - 1));
                    onSalute?.(false, result.salute_count);
                }
            } else {
                // 致敬
                setIsAnimating(true);

                // 播放音效（可选）
                playEngineSound();

                const result = await saluteLegend(legendId);
                if (result.success) {
                    setSaluted(true);
                    setCount(prev => prev + 1);
                    onSalute?.(true, result.salute_count, result.milestone_achieved);

                    // 检查是否达成里程碑
                    if (result.milestone_achieved) {
                        // 触发里程碑庆祝事件
                        window.dispatchEvent(new CustomEvent('milestone:achieved', {
                            detail: result.milestone_achieved
                        }));
                    }
                }

                // 动画结束后重置状态
                setTimeout(() => setIsAnimating(false), 800);
            }
        } catch (err) {
            console.error('Salute failed:', err);
        } finally {
            setIsLoading(false);
        }
    }, [legendId, saluted, isLoggedIn, isLoading, openAuthModal, onSalute]);

    // 播放引擎声效
    const playEngineSound = () => {
        // 检查用户是否开启了音效
        const soundEnabled = localStorage.getItem('salute_sound') !== 'false';
        if (!soundEnabled) return;

        try {
            // 创建简单的引擎轰鸣音效
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.setValueAtTime(80, audioContext.currentTime);
            oscillator.frequency.exponentialRampToValueAtTime(40, audioContext.currentTime + 0.3);

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (err) {
            // 音效播放失败不影响主功能
            console.warn('Audio playback failed:', err);
        }
    };

    const sizeClass = styles[size] || styles.medium;
    const saluteLabels = {
        saluted: { zh: '已致敬', en: 'Saluted', de: 'Gegrüßt' },
        salute: { zh: '致敬', en: 'Salute', de: 'Grüßen' }
    };
    const text = saluted
        ? (saluteLabels.saluted[language] || saluteLabels.saluted.en)
        : (saluteLabels.salute[language] || saluteLabels.salute.en);

    return (
        <button
            className={`
                ${styles.saluteButton}
                ${sizeClass}
                ${saluted ? styles.saluted : ''}
                ${isAnimating ? styles.animating : ''}
                ${className}
            `}
            onClick={handleClick}
            disabled={isLoading}
            aria-label={text}
            title={text}
        >
            {/* 火焰粒子效果 */}
            {isAnimating && (
                <div className={styles.particles}>
                    {[...Array(8)].map((_, i) => (
                        <span
                            key={i}
                            className={styles.particle}
                            style={{
                                '--angle': `${i * 45}deg`,
                                '--delay': `${i * 0.05}s`,
                            }}
                        />
                    ))}
                </div>
            )}

            {/* 主图标 */}
            <span className={styles.icon}>
                🤟
            </span>

            {/* 数字 */}
            {showCount && (
                <span className={styles.count}>
                    {formatNumber(count)}
                </span>
            )}

            {/* 文字 */}
            {showText && (
                <span className={styles.text}>
                    {text}
                </span>
            )}

            {/* 光晕效果 */}
            <span className={styles.glow} />
        </button>
    );
}
